import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Clock, Building2, RefreshCw, Home as HomeIcon, PauseCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';
import { usePageMeta } from '@/hooks/usePageMeta';

// Dedicated organizer application status page (Organizer V1 — Phase 1). Authenticated
// only. Always fetches the latest status from GET /api/users/me/organizer-application on
// open so an admin approve/reject/request-changes is reflected on reload. For organizers
// the rejection reason / request-changes message ARE shown to the applicant (guidance).
const RESUBMIT_STATUSES = ['rejected', 'request_changes'];

export default function OrganizerStatus() {
  usePageMeta('Organizer Status', 'Your organizer application status on SoundWolves.');
  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState(null);
  const [redirect, setRedirect] = useState(null);   // '/login' | '/' | null

  useEffect(() => {
    if (!api.isAuthenticated()) { setRedirect('/login'); setLoading(false); return; }
    let active = true;
    setLoading(true);
    api.get('/users/me/organizer-application')
      .then((a) => { if (active) { setApp(a || null); setLoading(false); } })
      .catch(() => { if (active) { setRedirect('/'); setLoading(false); } });   // 404 => go home
    return () => { active = false; };
  }, []);

  if (redirect) return <Navigate to={redirect} replace />;
  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-24 flex items-center justify-center" data-testid="organizer-status-loading">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const status = app?.status || null;
  let icon = Clock, heading = '', note = null, action = null;

  if (status === 'pending') {
    icon = Clock;
    heading = 'Your organizer application is under review';
    action = (
      <Link to="/"><Button variant="outline" data-testid="organizer-status-home"><HomeIcon className="w-4 h-4 mr-2" />Back to Home</Button></Link>
    );
  } else if (status === 'approved') {
    icon = CheckCircle2;
    heading = 'Your organizer profile is approved';
    note = 'Your full organizer dashboard is coming soon.';
    action = (
      <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
        <Link to="/organizer/profile"><Button variant="premium" data-testid="organizer-status-profile"><Building2 className="w-4 h-4 mr-2" />Organizer Profile</Button></Link>
        <Link to="/organizer/events"><Button variant="outline" data-testid="organizer-status-events">Organizer Events</Button></Link>
      </div>
    );
  } else if (status === 'request_changes') {
    icon = RefreshCw;
    heading = 'The reviewer requested changes';
    note = app?.request_changes_message || null;
    action = (
      <Link to="/organizer-apply"><Button variant="premium" data-testid="organizer-status-resubmit"><RefreshCw className="w-4 h-4 mr-2" />Update & Resubmit</Button></Link>
    );
  } else if (status === 'rejected') {
    icon = RefreshCw;
    heading = 'Your organizer application was not approved';
    note = app?.rejection_reason || null;
    action = (
      <Link to="/organizer-apply"><Button variant="premium" data-testid="organizer-status-resubmit"><RefreshCw className="w-4 h-4 mr-2" />Edit & Resubmit</Button></Link>
    );
  } else if (status === 'suspended') {
    icon = PauseCircle;
    heading = 'Your organizer profile is currently suspended';
    action = <Link to="/"><Button variant="outline"><HomeIcon className="w-4 h-4 mr-2" />Back to Home</Button></Link>;
  } else {
    icon = Clock;
    heading = 'Your organizer application status is unavailable.';
    action = <Link to="/"><Button variant="outline"><HomeIcon className="w-4 h-4 mr-2" />Back to Home</Button></Link>;
  }
  const Icon = icon;

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8" data-testid="organizer-status-page">
      <div className="container mx-auto px-4 max-w-xl">
        <Card className="glass-card border-border/50">
          <CardContent className="p-8 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/15 mx-auto mb-5 flex items-center justify-center">
              <Icon className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold mb-4" data-testid="organizer-status-heading">{heading}</h1>
            {note && (
              <div className="mb-6 mx-auto max-w-md rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700" data-testid="organizer-status-note">
                {note}
              </div>
            )}
            {RESUBMIT_STATUSES.includes(status) && !note && (
              <p className="text-sm text-muted-foreground mb-6">You can edit your application and submit it again.</p>
            )}
            <div className="flex justify-center">{action}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
